import Image from 'next/image'

import { Card, CardContent } from '@/components/ui/card'
import { useDictionary } from '@/lib/i18n/provider'
import { resolveImageSrc } from '@/lib/image-src'
import { cn } from '@/lib/utils'
import type { BrandProduct } from '@/types/website'

/**
 * Каталогийн барааны карт.
 *
 * Зурагны талбай нь тогтмол өндөртэй (h-56) бөгөөд зураг нь түүнийг
 * дүүргэж багтдаг (fill + object-contain). Эх зургийн хэмжээ ямар ч
 * байсан бүх карт нэгэн жигд, ижил өндөртэй харагдана — таслахгүй.
 */
export const CatalogProductCard = ({ product, view = 'grid' }: { product: BrandProduct; view?: 'grid' | 'list' }) => {
  if (view === 'list') {
    return (
      <Card className='flex flex-row items-center gap-4 p-4'>
        <div className='bg-muted relative size-24 shrink-0 overflow-hidden rounded-lg'>
          <ProductImage product={product} className='p-2' />
        </div>

        <h3 className='min-w-0 font-medium text-pretty'>{product.name}</h3>
      </Card>
    )
  }

  return (
    <Card className='group flex h-full flex-col border pt-0 ring-0 transition-colors'>
      {/* Барааны зураг өөрөө цагаан дэвсгэртэй ирдэг тул саарал хайрцаг хэрэггүй */}
      <div className='relative h-56 overflow-hidden rounded-t-xl'>
        <ProductImage
          product={product}
          className='p-5 transition-transform duration-500 group-hover:scale-105'
          priority={false}
        />
      </div>

      <CardContent>
        <h3 className='leading-snug font-medium text-pretty'>{product.name}</h3>
      </CardContent>
    </Card>
  )
}

/** Зураг нь эцэг талбайгаа бүрэн дүүргэнэ — жижиг зураг ч томорно. */
const ProductImage = ({
  product,
  className,
  priority
}: {
  product: BrandProduct
  className?: string
  priority?: boolean
}) => {
  const t = useDictionary()
  const src = resolveImageSrc(product.image_url)

  return src ? (
    <Image
      src={src}
      alt={product.name ?? ''}
      fill
      priority={priority}
      sizes='(min-width: 1280px) 25vw, (min-width: 640px) 50vw, 100vw'
      className={cn('object-contain', className)}
    />
  ) : (
    <div className='text-muted-foreground absolute inset-0 flex items-center justify-center text-xl'>
      {t.products.noImage}
    </div>
  )
}
